import {
  AgentMessageRequest,
  AIModel,
  Decor8PrimeWallsRequest,
  Decor8UpscaleRequest,
  ExtendMusicRequest,
  GenerateImageDecor8Request,
  GenerateMusicRequest,
  GenerateMusicResponse,
  GenerateResponse,
  GenerateTextToSpeechRequest,
  GenerateVideoRequest,
  GenerateVideoResponse,
  GenerateWavRequest,
  IRequestImage,
  PresentationRequest,
  RecordInfoResponse,
  TModelSearch,
  TModelSearchResponse,
  WavGenerationResponse,
} from "types";
import { api } from "./base.api";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

export const aiAPI = {
  getModels: async () => {
    const response = await api.get<AIModel[]>("/ai/models");
    return response.data;
  },

  generateImageRunware: async (request: IRequestImage, sessionId: string) => {
    const response = await api.post<ArrayBuffer>("/ai/image/runware/generate", {
      ...request,
      sessionId,
    });
    return response.data;
  },

  searchModelsRunware: async (request: TModelSearch) => {
    const response = await api.post<TModelSearchResponse>(
      "/ai/image/runware/search",
      {
        ...request,
      }
    );
    return response.data;
  },

  generateImageDecor8: async (request: GenerateImageDecor8Request) => {
    const response = await api.post<ArrayBuffer>("/ai/image/decor8/generate", {
      ...request,
    });
    return response.data;
  },

  upscaleImageDecor8: async (request: Decor8UpscaleRequest) => {
    const response = await api.post<ArrayBuffer>("/ai/image/decor8/upscale", {
      ...request,
    });
    return response.data;
  },

  generateImageDecor8PrimeWalls: async (request: Decor8PrimeWallsRequest) => {
    const response = await api.post<ArrayBuffer>(
      "/ai/image/decor8/prime-walls",
      {
        ...request,
      }
    );
    return response.data;
  },

  generatePresentation: async (request: PresentationRequest) => {
    const response = await api.post<ArrayBuffer>(
      "/ai/presentation/generate",
      request,
      {
        responseType: "arraybuffer",
        headers: {
          Accept:
            "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        },
      }
    );
    // response is a buffer
    return response.data;
  },

  generateResponse: async (
    prompt: string,
    model: string,
    temperature: number,
    memory: number,
    sessionId: string,
    onChunk?: (chunk: GenerateResponse) => void
  ) => {
    if (onChunk) {
      const response = await fetch(`${API_URL}/ai/chat/generate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          prompt,
          model,
          stream: true,
          temperature,
          memory,
          sessionId,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        if (error.message === "Credits limit exceeded") {
          throw new Error("Credits limit exceeded");
        }
        throw new Error(error.message || "Failed to generate response");
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error("Failed to get response reader");
      }

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split("\n");

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.slice(6));
              onChunk(data);
            } catch (error) {
              console.error("Error parsing SSE data:", error);
            }
          }
        }
      }
    } else {
      const response = await api.post<GenerateResponse>("/ai/chat/generate", {
        prompt,
        model,
        stream: false,
        temperature,
      });
      return response.data;
    }
  },

  generateAgentMessage: async (request: AgentMessageRequest) => {
    const response = await api.post<GenerateResponse>("/ai/chat/agent", {
      ...request,
    });
    return response.data;
  },

  generateWithTools: async (
    prompt: string,
    model: string,
    temperature: number,
    memory: number,
    sessionId: string,
    onChunk?: (chunk: GenerateResponse) => void
  ) => {
    if (onChunk) {
      const response = await fetch(`${API_URL}/ai/chat/tools`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          prompt,
          model,
          stream: true,
          temperature,
          memory,
          sessionId,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        if (error.message === "Credits limit exceeded") {
          throw new Error("Credits limit exceeded");
        }
        throw new Error(error.message || "Failed to generate response");
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error("Failed to get response reader");
      }

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split("\n");

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.slice(6));
              onChunk(data);
            } catch (error) {
              console.error("Error parsing SSE data:", error);
            }
          }
        }
      }
    }
  },

  generateMultiResponse: async (
    prompt: string,
    models: string[],
    temperatures: number[],
    memories: number[],
    sessionId: string,
    onChunk?: (chunk: GenerateResponse) => void
  ) => {
    if (onChunk) {
      const response = await fetch(`${API_URL}/ai/chat/multi`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          prompt,
          model: models[0], // Primary model
          models, // All models
          stream: true,
          temperature: temperatures[0], // Primary temperature
          temperatures, // All temperatures
          memory: memories[0], // Primary memory
          memories, // All memories
          sessionId,
          indexes: models.length, // Number of parallel streams
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        if (error.message === "Credits limit exceeded") {
          throw new Error("Credits limit exceeded");
        }
        throw new Error(error.message || "Failed to generate response");
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error("Failed to get response reader");
      }

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split("\n");

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.slice(6));
              onChunk(data);
            } catch (error) {
              console.error("Error parsing SSE data:", error);
            }
          }
        }
      }
    } else {
      const response = await api.post<GenerateResponse>("/ai/chat/multi", {
        prompt,
        model: models[0],
        models,
        stream: false,
        temperature: temperatures[0],
        temperatures,
        memory: memories[0],
        memories,
        sessionId,
        indexes: models.length,
      });
      return response.data;
    }
  },

  generateImage: async (prompt: string, model: string) => {
    const response = await api.post<GenerateResponse>("/ai/generate-image", {
      prompt,
      model,
    });
    return response.data;
  },

  generateVideo: async (
    promptImages: { uri: string; position: "first" | "last" }[],
    promptText: string,
    duration: 5 | 10,
    messageId: string
  ) => {
    const requestBody: GenerateVideoRequest = {
      promptImage: promptImages,
      promptText,
      model: "gen3a_turbo",
      duration,
      watermark: false,
      messageId,
    };
    const response = await api.post<GenerateVideoResponse>(
      "/ai/video/runway/generate",
      requestBody
    );
    return response.data;
  },

  getVideoStatus: async (taskId: string) => {
    const response = await api.get<GenerateVideoResponse>(
      `/ai/video/runway/status/${taskId}`
    );
    return response.data;
  },

  generateAudioTTS: async (
    request: GenerateTextToSpeechRequest,
    sessionId: string
  ) => {
    const response = await api.post<ArrayBuffer>("/ai/audio/tts/generate", {
      ...request,
      sessionId,
    });
    return response.data;
  },

  generateMusic: async (request: GenerateMusicRequest) => {
    const response = await api.post<GenerateMusicResponse>(
      "/ai/music/generate",
      request
    );
    return response.data;
  },

  extendMusic: async (request: ExtendMusicRequest) => {
    const response = await api.post<GenerateMusicResponse>(
      "/ai/music/extend",
      request
    );
    return response.data;
  },

  generateWav: async (request: GenerateWavRequest) => {
    const response = await api.post<WavGenerationResponse>(
      "/ai/music/wav",
      request
    );
    return response.data;
  },

  getRecordInfo: async (recordId: string) => {
    const response = await api.get<RecordInfoResponse>(
      `/ai/music/record/${recordId}`
    );
    return response.data;
  },
};
